/**
 * Pure axis-mirror angle math — the geometric core shared by every
 * MIRRTEXT-gated rotation transform in the command engine.
 *
 * Extracted from the private closures inside {@link geometryTransforms}'s
 * `mirrorTextReadable` / `mirrorTextFull` (Codex #342) so the SAME angle rule
 * can be reused, byte-identically, for entity kinds that carry a BARE rotation
 * scalar with no companion direction vector / generation-flag bits — DIMENSION's
 * `textRotation`, MULTILEADER's `textRotation` (CONTEXT_DATA MTextData rotation,
 * group 42), and a block-attribute's WORLD `overrideRotation` (EATTEDIT absolute
 * rotation override). See {@link ./mirrorRotationField} for the degenerate-axis
 * guarded wrapper those three call.
 *
 * ZERO internal dependencies (this module imports nothing from the package) so
 * `geometryTransforms.ts` can import FROM here without creating an import
 * cycle — `mirrorRotationField.ts` depends on BOTH this file and
 * `geometryTransforms.ts` (for `MIRROR_AXIS_DEGENERATE_LENSQ`), so the reverse
 * edge must not exist.
 */

type Point2D = { x: number; y: number };

/** Normalise an angle in degrees into the [0, 360) range. */
export function normalizeDeg360(angleDeg: number): number {
  const m = angleDeg % 360;
  return m < 0 ? m + 360 : m;
}

/**
 * |cos(angle)| below this treats a readable-mirror baseline as (near-)vertical,
 * where Math.cos's FP sign at 90°/270° is unreliable (~±1.8e-16). At/below it we
 * pick the readable ray from sin (UP) instead of the cos sign — explicit, not
 * FP-luck. Generous (1e-6) so genuinely vertical text is always disambiguated.
 * Byte-identical threshold to the one `mirrorTextReadable` used before
 * extraction (Codex #342).
 */
const MIRROR_VERTICAL_COS_EPS = 1e-6;

/**
 * Pure geometric reflection of a baseline/rotation angle `θ` across an axis at
 * angle `α` (both degrees): `2α − θ`, normalised to [0, 360). This is the
 * MIRRTEXT=1 (full reflection) rule for a bare rotation scalar — TEXT's
 * `rotation` field ({@link mirrorTextFull} in `geometryTransforms.ts`) and
 * DIMENSION/MULTILEADER/ATTRIB's bare rotation fields
 * ({@link "./mirrorRotationField"}) both reduce to this.
 */
export function reflectAngleAcrossAxis(
  angleDeg: number,
  axisAngleDeg: number,
): number {
  return normalizeDeg360(2 * axisAngleDeg - angleDeg);
}

/**
 * MIRRTEXT=0 "readable" rotation for a bare angle field: reflect the angle
 * across the axis, then add 180° when the reflected angle would read backward
 * (`cos(reflected) < 0`) — the exact rule `mirrorTextReadable` uses for TEXT's
 * `rotation` before Codex #342's extraction, generalised to any bare rotation
 * scalar (no companion direction vector to keep in sync).
 *
 * Returns BOTH the readable angle and whether the +180° adjustment fired — the
 * `flipped` flag is what a caller with a companion direction vector (MTEXT's
 * `xAxisDirection`) needs to bring the vector onto the same forward half-plane;
 * bare-rotation callers (DIMENSION/MULTILEADER/ATTRIB) only need `.angle`.
 *
 * NOT self-inverse: the +180° readability adjustment is many-to-one (it
 * collapses a backward-pointing reflection onto its forward twin), so applying
 * this twice does not always recover the original angle — callers that support
 * undo must snapshot the pre-mutation value (mirrors TEXT's
 * `restoreRotations` map in `geometryReducerEdit.ts`).
 */
export function readableAngleAcrossAxis(
  angleDeg: number,
  axisAngleDeg: number,
): { angle: number; flipped: boolean } {
  const reflected = normalizeDeg360(2 * axisAngleDeg - angleDeg);
  const rad = (reflected * Math.PI) / 180;
  const cos = Math.cos(rad);
  if (Math.abs(cos) < MIRROR_VERTICAL_COS_EPS) {
    // targeted-cad-mutation-testing EQUIVALENT MUTANT (skill-mandated format):
    //   mutator: EqualityOperator, `Math.sin(rad) >= 0` → `Math.sin(rad) > 0`.
    //   Why equivalent: this branch is only reached when `Math.abs(cos) <
    //   MIRROR_VERTICAL_COS_EPS` (1e-6) just above — by the trig identity
    //   sin²+cos²=1, that forces `|sin(rad)| > sqrt(1-1e-12) ≈
    //   0.9999999999995`, i.e. `sin(rad)` is bounded AWAY from 0 by ~13 orders
    //   of magnitude whenever this line executes. `>=0` and `>0` can only
    //   disagree at `sin(rad) === 0` exactly, which the entry guard makes
    //   unreachable — no schema-valid OR adversarially-constructed (rad still
    //   has to satisfy the guard above) input can distinguish them.
    //   Invariant: sin²(rad) + cos²(rad) = 1 (verified: Stryker/targeted
    //   mutation run on commit 73bdd2de — mutant confirmed unreachable-
    //   different by this proof, not "hard to test").
    return Math.sin(rad) >= 0
      ? { angle: reflected, flipped: false }
      : // targeted-cad-mutation-testing EQUIVALENT MUTANT:
        //   mutator: ArithmeticOperator, `reflected + 180` → `reflected - 180`.
        //   Why equivalent: `normalizeDeg360` reduces mod 360, and `+180` /
        //   `-180` differ by exactly one full period (360°), so
        //   `normalizeDeg360(x+180) ≡ normalizeDeg360(x-180)` for EVERY finite
        //   `x` — a direct algebraic consequence of `normalizeDeg360`'s own
        //   definition (`x % 360`, then `+360` if negative), not an
        //   approximation. Proven equivalent for all inputs, not just this
        //   call site (the identical mutant class also fires at the mirrored
        //   line below).
        { angle: normalizeDeg360(reflected + 180), flipped: true };
  }
  // targeted-cad-mutation-testing EQUIVALENT MUTANT:
  //   mutator: EqualityOperator, `cos < 0` → `cos <= 0`.
  //   Why equivalent: this line is reached ONLY when the `if` above did
  //   NOT fire, i.e. `Math.abs(cos) >= MIRROR_VERTICAL_COS_EPS` (1e-6) — so
  //   `cos` is guaranteed nonzero (in fact bounded away from 0 by 1e-6) by
  //   construction whenever this comparison runs. `<` and `<=` can only
  //   disagree at `cos === 0` exactly, which is excluded by the guard above.
  //   Invariant: the two branches are mutually exclusive and exhaustive on
  //   `|cos| < eps` vs `|cos| >= eps`, so `cos === 0` (which implies
  //   `|cos| < eps`) can never reach this line.
  return cos < 0
    ? { angle: normalizeDeg360(reflected + 180), flipped: true } // see ArithmeticOperator equivalence above — same proof.
    : { angle: reflected, flipped: false };
}

/** The axis angle (degrees) of the mirror line `line1` → `line2`, via atan2. */
export function mirrorAxisAngleDeg(line1: Point2D, line2: Point2D): number {
  return (
    (Math.atan2(line2.y - line1.y, line2.x - line1.x) * 180) / Math.PI
  );
}
